import { useFeatureAccess } from "../../access-control/hooks/useFeatureAccess";
import { useAuthContext } from "../../auth/state/AuthContext";
import AdminDashboardManagementLinks from "../components/dashboard/AdminDashboardManagementLinks";
import AdminDashboardSectionCard from "../components/dashboard/AdminDashboardSectionCard";
import { useAdminDashboard } from "../hooks/useAdminDashboard";
import { CRON_JOB_LOG_ENVIRONMENTS, type CronJobLogEnvironment } from "../models/admin-monitoring.models";
import { adminStyles } from "../styles/admin.styles";
import { ADMIN_DASHBOARD_SECTIONS } from "../utils/admin-dashboard.constants";
import { canViewAdminDashboardSection } from "../utils/admin-dashboard.utils";
import { getDefaultAdminMonitoringEnvironment } from "../utils/admin-monitoring-environment.utils";
import { ArrowPathIcon } from "@heroicons/react/24/outline";
import { useState } from "react";
import { twMerge } from "tailwind-merge";

// Give admins a quiet starting point across the existing sections, while preserving each source's permissions.
// Monitoring can inspect another environment; administration APIs always describe the current app.
const AdminDashboardPage = () => {
  const { state } = useAuthContext();
  const { hasFeature } = useFeatureAccess();
  const groups = state.user?.accessToken?.groups ?? [];
  const [environment, setEnvironment] = useState<CronJobLogEnvironment>(getDefaultAdminMonitoringEnvironment);
  const sections = ADMIN_DASHBOARD_SECTIONS.filter((section) =>
    canViewAdminDashboardSection(section, groups, hasFeature),
  );
  const sectionIds = sections.map((section) => section.id).join(",");
  const { results, isLoading, refresh } = useAdminDashboard(
    sectionIds,
    environment,
    state.user?.accessToken?.sub ?? "",
  );
  const monitoringSections = sections.filter((section) => section.scope === "monitoring");
  const appSections = sections.filter((section) => section.scope === "app");

  return (
    <main
      data-test="admin-dashboard-page"
      className={twMerge(adminStyles.page.pagePadding, "h-full min-w-0 flex-1 space-y-7 overflow-y-auto")}
    >
      <header className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className={adminStyles.heading.pageTitle}>Dashboard</h1>
          <p className="mt-2 text-sm opacity-60">The main signals across OpsLink administration.</p>
        </div>
        <button
          type="button"
          onClick={refresh}
          disabled={isLoading}
          data-test="admin-dashboard-refresh"
          className="inline-flex h-10 items-center gap-2 border border-black/15 px-4 text-xs font-semibold hover:border-green disabled:opacity-50 dark:border-white/20"
        >
          <ArrowPathIcon aria-hidden="true" className={twMerge("h-4 w-4", isLoading && "motion-safe:animate-spin")} />
          {isLoading ? "Refreshing" : "Refresh"}
        </button>
      </header>

      {monitoringSections.length > 0 && (
        <section aria-labelledby="admin-dashboard-monitoring-title" data-test="admin-dashboard-monitoring">
          <div className="mb-4 flex items-center justify-between gap-4">
            <h2 id="admin-dashboard-monitoring-title" className="text-sm font-semibold">
              Monitoring
            </h2>
            <label className="flex items-center gap-2 text-xs opacity-75">
              Environment
              <select
                value={environment}
                onChange={(event) => setEnvironment(event.target.value as CronJobLogEnvironment)}
                data-test="admin-dashboard-environment"
                className="h-8 border border-black/15 bg-white px-2 text-xs outline-none focus:border-green dark:border-white/20 dark:bg-gray-800"
              >
                {CRON_JOB_LOG_ENVIRONMENTS.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </label>
          </div>
          <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
            {monitoringSections.map((section) => (
              <AdminDashboardSectionCard
                key={section.id}
                section={section}
                result={results[section.id]}
                path={`${section.path}?environment=${environment}`}
              />
            ))}
          </div>
        </section>
      )}

      <section aria-labelledby="admin-dashboard-app-title" data-test="admin-dashboard-app-overview">
        <div className="mb-4 flex items-baseline gap-3">
          <h2 id="admin-dashboard-app-title" className="text-sm font-semibold">
            Administration
          </h2>
          <span className="text-xs opacity-50">Current app</span>
        </div>
        <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
          {appSections.map((section) => (
            <AdminDashboardSectionCard
              key={section.id}
              section={section}
              result={results[section.id]}
              path={section.path}
            />
          ))}
        </div>
      </section>

      <AdminDashboardManagementLinks />
    </main>
  );
};

export default AdminDashboardPage;
